// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { checkoutOrderSummaryStyle } from "@hardwoods/App/Widgets/Cart/OrderSummary.style";
import { cartLineBreakpoints } from "@hardwoods/Overrides/Widgets/Cart/helper";
import { xIsAuthenticated } from "@hardwoods/XDL/Utilities";
import color from "@hardwoods/XDL/Utilities/color";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canCheckoutWithCart,
    canSubmitRequisition,
    getCurrentCartState,
    isCartEmpty,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CartPageContext } from "@insite/content-library/Pages/CartPage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

const enum fields {
    showCartTotal = "showCartTotal",
    showFulfillmentMethod = "showFulfillmentMethod",
    showCartTotalButtons = "showCartTotalButtons",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showCartTotal]: boolean;
        [fields.showFulfillmentMethod]: boolean;
        [fields.showCartTotalButtons]: boolean;
    };
    extendedStyles?: OrderSummaryStyle;
}

export interface OrderSummaryStyle {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    cartTotalContainer?: InjectableCss;
    fulfillmentMethodContainer?: InjectableCss;
    cartTotalButtonsContainer?: InjectableCss;
}

const orderSummaryStyle: OrderSummaryStyle = {
    container: {
        css: css`
            width: 330px;
            ${xScreen.lte(cartLineBreakpoints.xs)} {
                width: 100%;
            }
        `,
    },
    title: {
        ...checkoutOrderSummaryStyle.title,
    },
    cartTotalContainer: {
        css: css`
            border: 1px solid ${color("common.border")};
            padding: 0;
            > button,
            > div:not(:first-child) {
                display: none;
            }
        `,
    },
    fulfillmentMethodContainer: {
        css: css`
            ${({ isButtonSectionShown }: any) =>
                !isButtonSectionShown &&
                css`
                    border-bottom: 1px solid ${color("common.border")};
                    border-radius: 0 0 4px 4px;
                `}
        `,
    },
    cartTotalButtonsContainer: {
        css: css`
            > div:first-child {
                display: none;
            }

            > div.buttons-wrapper {
                background-color: ${color("common.ui3")};
                border: 1px solid ${color("common.border")};
                border-top: 0;
                border-radius: 0 0 4px 4px;
                padding: 0 20px 20px;
            }

            > div.otherButtons-wrapper {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                padding: 20px 10px 0 45px;
                > button {
                    margin-bottom: 16px;
                }
            }
        `,
    },
};

const OrderSummary: FC<OwnProps> = ({
    fields: { title, showCartTotal, showCartTotalButtons, showFulfillmentMethod },
    extendedStyles,
}) => {
    const {
        canCheckoutWithCart: canCheckoutWithCartProps,
        canSubmitRequisition: canSubmitRequisitionProps,
        session,
        isCartEmpty: isCartEmptyProp,
    } = useAppSelector(state => {
        const cartState = getCurrentCartState(state);
        const cart = cartState.value;
        return {
            canCheckoutWithCart: canCheckoutWithCart(cart),
            session: getSession(state),
            isCartEmpty: isCartEmpty(cart),
            canSubmitRequisition: canSubmitRequisition(cart),
        };
    });

    const [styles] = useState(() => mergeWithCss(orderSummaryStyle, extendedStyles));

    if (isCartEmptyProp) {
        return null;
    }

    const isButtonSectionShown = canCheckoutWithCartProps || canSubmitRequisitionProps;
    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.title}>{title}</Typography>
            {showCartTotal && (
                <StyledWrapper {...styles.cartTotalContainer}>
                    {createWidgetElement("Cart/CartTotal", {
                        fields: {},
                    })}
                </StyledWrapper>
            )}
            {showFulfillmentMethod && xIsAuthenticated(session) && (
                <StyledWrapper {...styles.fulfillmentMethodContainer} {...{ isButtonSectionShown }}>
                    {createWidgetElement("Cart/CartFulfillmentMethodSelector", {
                        fields: {},
                    })}
                </StyledWrapper>
            )}
            {showCartTotalButtons && (
                <StyledWrapper {...styles.cartTotalButtonsContainer}>
                    {createWidgetElement("Cart/CartTotal", {
                        fields: {},
                    })}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderSummary,
    definition: {
        displayName: "Order Summary",
        group: "Cart",
        allowedContexts: [CartPageContext],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Order Summary",
                fieldType: "General",
            },
            {
                name: fields.showCartTotal,
                displayName: "Cart Total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showFulfillmentMethod,
                displayName: "Fulfillment Method",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showCartTotalButtons,
                displayName: "Cart Total Buttons",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isEnabled: item => false,
            },
        ],
    },
};

export default widgetModule;
